import React from 'react';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
    size?: 'sm' | 'md' | 'lg';
    isLoading?: boolean;
    icon?: React.ReactNode;
    fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
    children,
    variant = 'primary',
    size = 'md',
    isLoading,
    icon,
    className = '',
    disabled,
    fullWidth = false,
    ...props
}) => {
    // Base styles for layout and transition
    const baseStyles = "relative inline-flex items-center justify-center font-medium transition-all duration-200 ease-smooth disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none select-none";

    // Focus ring styles
    const focusStyles = "focus:outline-none focus:ring-2 focus:ring-offset-2";

    // Variants mapping to Tailwind/Design System classes
    const variants = {
        primary: `btn-premium bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] text-white shadow-md hover:shadow-lg border-transparent ${focusStyles} focus:ring-[var(--color-primary)]`,
        secondary: `bg-white text-[var(--color-primary)] border-2 border-[var(--color-primary)] hover:bg-teal-50 shadow-sm hover:shadow-md ${focusStyles} focus:ring-[var(--color-primary)]`,
        outline: `bg-transparent text-gray-700 border border-gray-300 hover:bg-gray-50 hover:text-gray-900 ${focusStyles} focus:ring-gray-400`,
        ghost: `bg-transparent text-[var(--color-primary)] hover:bg-teal-50 ${focusStyles} focus:ring-[var(--color-primary)]`,
        danger: `btn-premium bg-gradient-to-r from-[var(--color-error)] to-red-600 text-white shadow-md hover:shadow-lg border-transparent ${focusStyles} focus:ring-red-500`,
        success: `btn-premium bg-gradient-to-r from-[var(--color-success)] to-green-600 text-white shadow-md hover:shadow-lg border-transparent ${focusStyles} focus:ring-green-500`
    };

    // Sizes mapping
    const sizes = {
        sm: "px-3 py-1.5 text-sm rounded-lg gap-1.5",
        // 44px min height for touch targets
        md: "px-5 py-2.5 text-base rounded-xl gap-2 min-h-[44px]",
        lg: "px-8 py-3.5 text-lg rounded-xl gap-2.5 min-h-[52px]"
    };

    const widthClass = fullWidth ? 'w-full' : '';

    return (
        <button
            className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`}
            disabled={disabled || isLoading}
            {...props}
        >
            {isLoading && <Loader2 className="animate-spin" size={size === 'sm' ? 14 : 20} />}
            {!isLoading && icon && <span className="flex-shrink-0">{icon}</span>}
            <span>{children}</span>
        </button>
    );
};
